import { print } from "graphql";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { TOOLKIT_PRODUCT_ID } from "../config/toolkit-config";
import { requestGraphql } from "../queries/graphql-client";
import { headerLinksQuery } from "../queries/queries";

type LinksResponse = {
  readonly products?: ReadonlyArray<{
    readonly id: string;
    readonly modules?: {
      readonly custom_tables?: {
        readonly links?: ReadonlyArray<{
          readonly url?: string | null;
          readonly name?: string | null;
          readonly type?: string | null;
          readonly sort_no?: number | null;
        }>;
      };
    };
  }>;
};

export type ToolkitLink = {
  readonly name: string;
  readonly url: string;
  readonly type: string;
  readonly sortNo: number | null;
};

type NormalizedToolkitLink = ToolkitLink & {
  readonly index: number;
};

async function fetchToolkitLinks(): Promise<ReadonlyArray<ToolkitLink>> {
  const data = await requestGraphql<LinksResponse, Record<string, never>>(print(headerLinksQuery), {});
  const toolkitProduct = data.products?.find((product) => product.id === TOOLKIT_PRODUCT_ID);
  const links = toolkitProduct?.modules?.custom_tables?.links ?? [];

  return links
    .map<NormalizedToolkitLink>((link, index) => ({
      index,
      name: link.name?.trim() ?? "",
      sortNo: typeof link.sort_no === "number" ? link.sort_no : null,
      type: link.type?.trim().toLowerCase() ?? "",
      url: link.url?.trim() ?? "",
    }))
    .filter((link) => link.name.length > 0 && link.url.length > 0 && link.type.length > 0)
    .sort((first, second) => {
      if (first.sortNo !== null && second.sortNo !== null && first.sortNo !== second.sortNo) {
        return first.sortNo - second.sortNo;
      }

      if (first.sortNo !== null && second.sortNo === null) {
        return -1;
      }

      if (first.sortNo === null && second.sortNo !== null) {
        return 1;
      }

      return first.index - second.index;
    })
    .map(({ index: _index, ...link }) => link);
}

export function useToolkitLinks(type?: string): ReadonlyArray<ToolkitLink> {
  const normalizedType = type?.trim().toLowerCase();
  const query = useQuery({
    queryKey: ["toolkit-links", TOOLKIT_PRODUCT_ID],
    queryFn: fetchToolkitLinks,
    staleTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  return useMemo(() => {
    const links = query.data ?? [];

    if (!normalizedType) {
      return links;
    }

    return links.filter((link) => link.type === normalizedType);
  }, [normalizedType, query.data]);
}